import React, { useState, useEffect } from 'react';
import { categoriesApi, productsApi, Category } from '../../services/api';
import './CategoryManagement.css';

const CategoryManagement: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await categoriesApi.getAll();
            setCategories(response.data.categories);
        } catch (err) {
            setError('Failed to fetch categories');
            console.error('Error fetching categories:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Category name is required');
            return;
        }

        try {
            setError(null);
            if (editingCategory) {
                await categoriesApi.update(editingCategory.id, formData);
            } else {
                await categoriesApi.create(formData);
            }
            await fetchCategories();
            resetForm();
        } catch (err) {
            setError('Failed to save category');
            console.error('Error saving category:', err);
        }
    };

    const handleEdit = (category: Category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            description: category.description || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        // Check if category has items
        try {
            const productsResponse = await productsApi.getAll(id);
            const products = productsResponse.data.products || [];

            if (products.length > 0) {
                setError(`Cannot delete category. It has ${products.length} item(s) mapped to it. Please delete or move the items first.`);
                return;
            }

            if (!window.confirm('Are you sure you want to delete this category?')) {
                return;
            }

            await categoriesApi.delete(id);
            await fetchCategories();
            setError(null);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to delete category';
            setError(errorMessage);
            console.error('Error deleting category:', err);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', description: '' });
        setEditingCategory(null);
        setShowForm(false);
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="category-management">
            <div className="section-header">
                <h2>Category Management</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(true)}
                >
                    Add New Category
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="form-modal">
                    <div className="form-container">
                        <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Category Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                    placeholder="Enter category name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter category description"
                                    rows={3}
                                />
                            </div>

                            <div className="form-actions">
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingCategory ? 'Update' : 'Create'} Category
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="categories-grid">
                {categories.map((category) => (
                    <div key={category.id} className="category-card">
                        <div className="category-content">
                            <h3 className="category-name">{category.name}</h3>
                            {category.description && (
                                <p className="category-description">{category.description}</p>
                            )}
                            <div className="category-meta">
                                <span className="created-date">
                                    Created: {new Date(category.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                        <div className="category-actions">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleEdit(category)}
                            >
                                Edit
                            </button>
                            <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDelete(category.id)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {categories.length === 0 && (
                <div className="empty-state">
                    <p>No categories found. Create your first category to get started.</p>
                </div>
            )}
        </div>
    );
};

export default CategoryManagement;
